import { Component } from '@theme/component';

const CONSENT_EVENT = 'offsider:signature-consent-required';
const PROPERTY_TERMS = 'Aceptación Términos y Condiciones';
const PROPERTY_PRIVACY = 'Aceptación Política de Privacidad';
const PROPERTY_MARKETING = 'Comunicaciones comerciales';

/**
 * Modal de consentimiento legal antes de añadir al carrito (metafield custom.signature).
 *
 * @extends {Component}
 */
class OffsiderSignatureConsent extends Component {
  /** @type {HTMLElement | null} */
  #pendingProductForm = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CONSENT_EVENT, this.#onConsentRequired);
    this.#bindUi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CONSENT_EVENT, this.#onConsentRequired);
  }

  #onConsentRequired = (event) => {
    const productForm = event.detail?.productForm;
    if (!(productForm instanceof HTMLElement)) return;

    this.#pendingProductForm = productForm;
    this.#resetState();
    this.#getDialogHost()?.showDialog();
  };

  #bindUi() {
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-offsider-signature-checkbox]')) return;
      this.#updateConfirmState();
    });

    for (const button of this.querySelectorAll('[data-offsider-signature-cancel]')) {
      button.addEventListener('click', () => {
        this.#closeModal();
      });
    }

    const confirmButton = this.querySelector('[data-offsider-signature-confirm]');
    confirmButton?.addEventListener('click', () => {
      this.#handleConfirm();
    });

    const dialog = this.querySelector('[data-offsider-signature-dialog]');
    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener('close', () => {
        this.#pendingProductForm = null;
        this.#resetState();
      });
    }
  }

  #getDialogHost() {
    const host = this.querySelector('dialog-component');
    if (host && typeof host.showDialog === 'function') {
      return host;
    }
    return null;
  }

  #closeModal() {
    this.#getDialogHost()?.closeDialog();
    this.#pendingProductForm = null;
    this.#resetState();
  }

  #resetState() {
    const error = this.querySelector('[data-offsider-signature-error]');
    if (error instanceof HTMLElement) {
      error.textContent = '';
      error.classList.add('hidden');
    }

    for (const checkbox of this.querySelectorAll('[data-offsider-signature-checkbox]')) {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.checked = false;
        checkbox.disabled = false;
      }
    }

    this.#updateConfirmState();
  }

  #updateConfirmState() {
    const terms = this.querySelector('[data-offsider-signature-checkbox="terms"]');
    const privacy = this.querySelector('[data-offsider-signature-checkbox="privacy"]');
    const confirmButton = this.querySelector('[data-offsider-signature-confirm]');

    const termsAccepted = terms instanceof HTMLInputElement && terms.checked;
    const privacyAccepted = privacy instanceof HTMLInputElement && privacy.checked;

    if (confirmButton instanceof HTMLButtonElement) {
      confirmButton.disabled = !(termsAccepted && privacyAccepted);
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-offsider-signature-error]');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.classList.remove('hidden');
    error.focus();
  }

  #handleConfirm() {
    const productForm = this.#pendingProductForm;
    if (!(productForm instanceof HTMLElement)) return;

    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;

    const terms = this.querySelector('[data-offsider-signature-checkbox="terms"]');
    const privacy = this.querySelector('[data-offsider-signature-checkbox="privacy"]');
    const marketing = this.querySelector('[data-offsider-signature-checkbox="marketing"]');

    if (!(terms instanceof HTMLInputElement) || !(privacy instanceof HTMLInputElement)) return;

    if (!terms.checked || !privacy.checked) {
      this.#showError(this.#translation('error_required'));
      return;
    }

    const timestamp = new Date().toISOString();
    this.#setLineItemProperty(form, PROPERTY_TERMS, `Sí (${timestamp})`);
    this.#setLineItemProperty(form, PROPERTY_PRIVACY, `Sí (${timestamp})`);

    if (marketing instanceof HTMLInputElement && marketing.checked) {
      this.#setLineItemProperty(form, PROPERTY_MARKETING, `Sí (${timestamp})`);
    } else {
      this.#removeLineItemProperty(form, PROPERTY_MARKETING);
    }

    productForm.dataset.signatureConsentGranted = 'true';
    this.#closeModal();

    window.requestAnimationFrame(() => {
      window.OffsiderSpdSplitPayment?.prepareSpdBeforeAddToCart?.();
      window.OffsiderSellingPlanPayment?.prepareBeforeAddToCart?.();
      form.requestSubmit();
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #setLineItemProperty(form, name, value) {
    this.#removeLineItemProperty(form, name);

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    input.dataset.offsiderSignatureProperty = 'true';
    form.appendChild(input);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   */
  #removeLineItemProperty(form, name) {
    const selector = `input[data-offsider-signature-property][name="properties[${name}]"]`;
    form.querySelector(selector)?.remove();
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #translation(key) {
    const translations = window.Theme?.translations?.offsiderSignatureConsent;
    if (translations && translations[key]) {
      return translations[key];
    }

    const fallback = {
      error_required: 'Debes aceptar los Términos y Condiciones y la Política de Privacidad.',
    };

    return fallback[key] || '';
  }
}

if (!customElements.get('offsider-signature-consent')) {
  customElements.define('offsider-signature-consent', OffsiderSignatureConsent);
}
